import { sha256 } from "@noble/hashes/sha256";
import { hmac } from "@noble/hashes/hmac";
import { bytesToHex, hexToBytes, utf8ToBytes, randomBytes } from "@noble/hashes/utils";
import { ed25519 } from "@noble/curves/ed25519";

export { bytesToHex, hexToBytes, utf8ToBytes, randomBytes };

export function sha256Hex(data: Uint8Array | string): string {
  return bytesToHex(sha256(typeof data === "string" ? utf8ToBytes(data) : data));
}

// The id shape used everywhere: pubkeys, op hashes, group ids, ghost ids.
export function isHex64(value: unknown): value is string {
  return typeof value === "string" && /^[0-9a-f]{64}$/.test(value);
}

// ---------------------------------------------------------------------------
// Identity: one Ed25519 keypair per device. The pubkey hex IS the member id.
// ---------------------------------------------------------------------------

export interface Identity {
  privKey: string; // hex, never leaves the device except via explicit backup
  pubKey: string; // hex, this is the member id in every op
}

export function generateIdentity(): Identity {
  const priv = ed25519.utils.randomPrivateKey();
  return {
    privKey: bytesToHex(priv),
    pubKey: bytesToHex(ed25519.getPublicKey(priv)),
  };
}

export function signHex(privKeyHex: string, message: Uint8Array): string {
  return bytesToHex(ed25519.sign(message, hexToBytes(privKeyHex)));
}

export function verifySig(sigHex: string, message: Uint8Array, pubKeyHex: string): boolean {
  try {
    return ed25519.verify(hexToBytes(sigHex), message, hexToBytes(pubKeyHex));
  } catch {
    return false;
  }
}

// Recover the public key (the member id) from a private key. Used when importing
// an identity backup: the pubKey field in the file is not trusted, it is
// re-derived so a mismatched backup cannot silently brick the identity.
export function publicKeyForPrivateKey(privKeyHex: string): string {
  return bytesToHex(ed25519.getPublicKey(hexToBytes(privKeyHex)));
}

// ---------------------------------------------------------------------------
// Group key + envelope encryption. The 32-byte group key travels only inside
// invite links/QR codes (URL fragment - never sent to any server). Everything
// a relay stores is an Envelope: AES-256-GCM ciphertext + nonce.
// ---------------------------------------------------------------------------

export function generateGroupKey(): Uint8Array {
  return randomBytes(32);
}

// Relay-visible mailbox id. Derived via HMAC so the relay cannot correlate a
// topic back to a group id or key even if it learns one of them elsewhere.
export function deriveTopic(groupKey: Uint8Array): string {
  return bytesToHex(hmac(sha256, groupKey, utf8ToBytes("psplit/topic/v1")));
}

export interface Envelope {
  v: 1;
  n: string; // base64url nonce (12 bytes)
  c: string; // base64url AES-256-GCM ciphertext
}

// Sync loops encrypt/decrypt many envelopes with the same group key; importing
// a CryptoKey per call is pure redundant async work. Memoized by key bytes
// (an app holds a handful of group keys, so the cache stays tiny).
const aesKeyCache = new Map<string, Promise<CryptoKey>>();

function importAesKey(raw: Uint8Array): Promise<CryptoKey> {
  if (raw.length !== 32) return Promise.reject(new Error("group key must be 32 bytes"));
  const cacheKey = bytesToHex(raw);
  let cached = aesKeyCache.get(cacheKey);
  if (!cached) {
    cached = crypto.subtle.importKey("raw", raw as BufferSource, "AES-GCM", false, [
      "encrypt",
      "decrypt",
    ]);
    aesKeyCache.set(cacheKey, cached);
  }
  return cached;
}

export async function encryptPayload(groupKey: Uint8Array, plaintext: Uint8Array): Promise<Envelope> {
  const key = await importAesKey(groupKey);
  const nonce = randomBytes(12);
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as BufferSource },
    key,
    plaintext as BufferSource,
  );
  return { v: 1, n: toB64Url(nonce), c: toB64Url(new Uint8Array(ct)) };
}

export async function decryptPayload(groupKey: Uint8Array, envelope: Envelope): Promise<Uint8Array> {
  const key = await importAesKey(groupKey);
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: fromB64Url(envelope.n) as BufferSource },
    key,
    fromB64Url(envelope.c) as BufferSource,
  );
  return new Uint8Array(pt);
}

// ---------------------------------------------------------------------------
// base64url without padding - URL-fragment- and QR-safe.
// ---------------------------------------------------------------------------

export function toB64Url(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function fromB64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
